import {
    unref,
    ComponentPublicInstance,
    Ref,
    onMounted,
    onUnmounted,
} from '@vue/composition-api';

/**
 * Maybe it's a ref, or a plain value
 *
 * ```ts
 * type MaybeRef<T> = T | Ref<T>
 * ```
 */
export type MaybeRef<T> = T | Ref<T>;

/**
 * Maybe it's a ref, or a plain value, or a getter function
 *
 * ```ts
 * type MaybeRefOrGetter<T> = (() => T) | T | Ref<T> | ComputedRef<T>
 * ```
 */
export type MaybeRefOrGetter<T> = MaybeRef<T> | (() => T);

export type MaybeElement = HTMLElement | SVGElement | ComponentPublicInstance | undefined | null;

export type MaybeElementRef<T extends MaybeElement = MaybeElement> = MaybeRef<T>;

export type MaybeComputedElementRef<T extends MaybeElement = MaybeElement> = MaybeRefOrGetter<T>;

export type UnRefElementReturn<T extends MaybeElement = MaybeElement> = T extends ComponentPublicInstance ? Exclude<MaybeElement, ComponentPublicInstance> : T | undefined;

/**
 * Get the DOM element of a Ref of an element or Vue component instance.
 *
 * @param elRef The Ref to get the DOM element of.
 * @returns The DOM element, or undefined if the Ref does not point to a DOM element.
 */
export function unrefElement<T extends MaybeElement>(elRef: MaybeComputedElementRef<T>): UnRefElementReturn<T> {
    const plain = unref(elRef);
    return (plain as ComponentPublicInstance)?.$el ?? plain;
}

export type CustomWindow = {
    window?: Window;
};

// I know we're not doing SSR but this is a good habit to get into, also sometimes buildtime/runtime environments get wonky in Storybook
const defaultWindow = typeof window !== 'undefined' ? window : undefined;

export type OnClickOutsideEvents = Pick<WindowEventMap, 'click' | 'mousedown' | 'mouseup' | 'touchstart' | 'touchend' | 'pointerdown' | 'pointerup'>;

export type OnClickOutsideOptions<E extends keyof OnClickOutsideEvents> = {
    event?: E
} & CustomWindow;

export function getParents(node: HTMLElement, memo: HTMLElement[] = []): HTMLElement[] {
    const parentNode = node.parentNode as HTMLElement | null;

    return parentNode
        ? getParents(parentNode, memo.concat([parentNode]))
        : memo;
}

/**
 * This function adds an event listener to the window that triggers a handler function when a click event occurs outside of a specified target element.
 *
 * @param target - The target element that the click event should occur outside of. This can be a Ref to an HTMLElement, SVGElement, Vue component instance, or a plain value of these types.
 * @param handler - The function to execute when the click event occurs outside the target element. The function receives the event as an argument.
 * @param options - An optional object that can specify the type of event to listen for and the window object to add the event listener to.
 *
 * @typeParam E - The type of event to listen for. This should be a key of the OnClickOutsideEvents type. Defaults to 'pointerdown'.
 *
 * @example
 * ```typescript
 * import { ref } from '@vue/composition-api';
 * import { onClickOutside } from './onClickOutside';
 *
 * export default {
 *   setup() {
 *     const divRef = ref(null);
 *
 *     onClickOutside(divRef, (event) => {
 *        console.log('Clicked outside the div!');
 *     });
 *
 *     return {
 *       divRef,
 *     };
 *   },
 * };
 * ```
 */
export function onClickOutside<E extends keyof OnClickOutsideEvents = 'pointerdown'>(
    target: MaybeElementRef,
    handler: (event: OnClickOutsideEvents[E]) => void,
    options: OnClickOutsideOptions<E> = {},
) {
    const { window = defaultWindow!, event = 'pointerdown' } = options;

    const eventPath = (event: OnClickOutsideEvents[E]) => {
        // @ts-expect-error https://developer.mozilla.org/en-US/docs/Web/API/Event/composedPath doesn't exist on Edge 18 or lower
        const path = ((event.composedPath && event.composedPath()) || event.path) as HTMLElement[] | undefined;

        if (path != null) {
            return path;
        }

        return [event.target].concat(getParents(event.target as HTMLElement));
    };

    const listener = (event: OnClickOutsideEvents[E]) => {
        const targetElement = unrefElement(target);

        if (!targetElement || targetElement === event.target || eventPath(event).includes(targetElement)) {
            return;
        }

        handler(event);
    };

    onMounted(() => {
        window.addEventListener(event as keyof WindowEventMap, listener as EventListener, { passive: true });
    });

    onUnmounted(() => {
        window.removeEventListener(event as keyof WindowEventMap, listener as EventListener);
    });
}
